import React from 'react';
import { useField } from 'formik';

import { styled } from '@mui/material/styles';

import { FormHelperText, InputLabel, TextField } from '@mui/material';

import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

const DesktopDatePickerStyled = styled(DesktopDatePicker)(() => ({
  '& .MuiInputAdornment-root': {
    backgroundColor: '#f5f5f5',
  },
}));

const TextFieldStyled = styled(TextField)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(0),
  },

  '& .MuiInputBase-input': {
    borderRadius: 4,
    backgroundColor: '#f5f5f5',
    fontSize: 16,
    fontWeight: 500,

    transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow']),

    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),

    cursor: 'pointer',
  },
}));

const isValidDate = (value) => {
  return value instanceof Date && !Number.isNaN(value.getTime());
};

const createLocalDate = (year, month, day) => {
  const date = new Date(year, month - 1, day);

  const isExactDate = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;

  return isExactDate ? date : null;
};

const parseDateValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (isValidDate(value)) {
    return value;
  }

  /*
   * Support Day.js values when a Day.js
   * localization adapter is used.
   */
  if (typeof value === 'object' && typeof value.toDate === 'function') {
    const converted = value.toDate();

    return isValidDate(converted) ? converted : null;
  }

  if (typeof value === 'string') {
    /*
     * Supports:
     * 2026-07-19
     * 2026-07-19T00:00:00Z
     */
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (match) {
      return createLocalDate(Number(match[1]), Number(match[2]), Number(match[3]));
    }
  }

  const fallbackDate = new Date(value);

  return isValidDate(fallbackDate) ? fallbackDate : null;
};

const padDatePart = (value) => {
  return String(value).padStart(2, '0');
};

const formatDateForApi = (value) => {
  const date = parseDateValue(value);

  if (!date) {
    return null;
  }

  const year = date.getFullYear();

  const month = padDatePart(date.getMonth() + 1);

  const day = padDatePart(date.getDate());

  return `${year}-${month}-${day}`;
};

const DesktopDatePickerBasic = ({
  name,
  required = false,
  label,

  /*
   * Explicitly discard props potentially
   * injected by FastField `as`.
   */
  value: _injectedValue,
  onChange: _injectedOnChange,
  onBlur: _injectedOnBlur,

  ...otherProps
}) => {
  const [field, meta, helpers] = useField(String(name));

  const pickerValue = parseDateValue(field.value);

  const hasError = Boolean(meta.touched && meta.error);

  const showHelperText = !field.name.startsWith('iteminv_invs');

  const handleChange = (newValue) => {
    const apiValue = formatDateForApi(newValue);

    void helpers.setValue(apiValue, true);
  };

  const handleBlur = () => {
    void helpers.setTouched(true, true);
  };

  return (
    <>
      {label && (
        <InputLabel
          htmlFor={field.name}
          shrink
          required={required}
          sx={{
            mb: -1,
            fontSize: 18,
            fontWeight: 700,
          }}
        >
          {label}
        </InputLabel>
      )}

      <DesktopDatePickerStyled
        {...otherProps}
        inputFormat="dd/MM/yyyy"
        value={pickerValue}
        onChange={handleChange}
        renderInput={(params) => (
          <TextFieldStyled
            {...params}
            id={field.name}
            name={field.name}
            fullWidth
            required={required}
            variant="outlined"
            size="small"
            error={hasError}
            onBlur={handleBlur}
          />
        )}
      />

      <FormHelperText
        error={hasError}
        sx={{
          color: 'red',
          fontSize: 14,
        }}
      >
        {showHelperText && hasError ? meta.error : null}
      </FormHelperText>
    </>
  );
};

export default DesktopDatePickerBasic;
